/**
 * Sysadmin Audit → Activity Stream (Wave 3 §10.2 #7 / spec §3.6).
 *
 * Cross-tenant audit_logs firehose with filters. Replaces the §10.2 #5
 * stub. Uses cursor pagination ("Load more" button) per anti-pattern
 * §8.6 — chains are continuous; we never page-number them. A
 * full virtualized infinite-scroll lands in Wave 4 when the volume
 * justifies it.
 *
 * Filters:
 *   - tenant (free-text UUID) — narrows to one tenant
 *   - action (free-text) — exact match on the action verb
 *   - outcome (success / failure)
 *   - category (free-text)
 *
 * Each row links to the resource (when resourceId is present).
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Activity, Filter, X, RefreshCw, Clock, ShieldAlert, ShieldCheck,
} from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import { StatusPill } from '@/components/ui/status-pill';
import {
  useAuditStream,
  type AuditStreamRow,
  type AuditStreamFilters,
} from '@/lib/use-sysadmin-audit';

export const Route = createFileRoute('/sysadmin/audit/stream')({
  component: AuditStreamPage,
});

function AuditStreamPage() {
  // Filter state (committed → triggers a fetch).
  const [committed, setCommitted] = useState<AuditStreamFilters>({ limit: 50 });
  // Draft state for the input fields (only commit on Apply).
  const [draft, setDraft] = useState<AuditStreamFilters>({ limit: 50 });

  // Accumulated rows across "Load more" cursor steps. Only populated by
  // explicit "Load more" clicks (when `committed.cursor` is set). The
  // initial / filtered list reads from `data?.items` directly via the
  // `rows` selector below — this avoids the bug #888 hydration race
  // where the page rendered before the useEffect could copy items into
  // accumulated, leaving the table empty AND the empty-state hidden
  // (because `accumulated` was empty but `data.items` had real rows).
  const [accumulated, setAccumulated] = useState<AuditStreamRow[]>([]);

  const { data, isLoading, error, refetch, isFetching } =
    useAuditStream(committed);

  // Append additional pages to `accumulated` when "Load more" runs.
  // Reset back to a clean slate whenever filters change (no cursor).
  useEffect(() => {
    if (!data) return;
    if (!committed.cursor) {
      // First page of a fresh filter set — clear the accumulator so a
      // future "Load more" doesn't carry over rows from a stale filter.
      setAccumulated([]);
      return;
    }
    setAccumulated((prev) => {
      const seen = new Set(prev.map((r) => r.cursor));
      const out = [...prev];
      for (const r of data.items) {
        if (!seen.has(r.cursor)) out.push(r);
      }
      return out;
    });
  }, [data, committed.cursor]);

  // Bug #888 — render rows directly from `data.items` on the first
  // page, fall through to the accumulator only when "Load more" has
  // contributed extra pages. This eliminates the initial paint where
  // both the table tbody AND the EmptyState were absent because the
  // setAccumulated effect hadn't run yet.
  const rows: AuditStreamRow[] =
    accumulated.length > 0 ? accumulated : (data?.items ?? []);

  const apply = () => {
    setCommitted({ ...draft, cursor: undefined });
  };

  const clear = () => {
    const cleared: AuditStreamFilters = { limit: 50 };
    setDraft(cleared);
    setCommitted(cleared);
  };

  const loadMore = () => {
    if (!data?.nextCursor) return;
    setCommitted({ ...committed, cursor: data.nextCursor });
  };

  const filtersDirty =
    JSON.stringify({ ...draft, cursor: undefined }) !==
    JSON.stringify({ ...committed, cursor: undefined });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Activity className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Activity Stream
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.6</Badge>
              <Badge variant="secondary" className="text-xs">v1</Badge>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setCommitted({ ...committed, cursor: undefined });
            refetch();
          }}
          disabled={isFetching}
        >
          <RefreshCw className={isFetching ? 'h-3.5 w-3.5 mr-1.5 animate-spin' : 'h-3.5 w-3.5 mr-1.5'} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
          <CardDescription>
            Narrow the firehose. All filters AND together. Cursor
            resets on Apply / Clear.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="f-tenant" className="text-xs">Tenant ID</Label>
              <Input
                id="f-tenant"
                value={draft.tenantId ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, tenantId: e.target.value || undefined })
                }
                placeholder="UUID"
                className="font-mono text-xs h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-action" className="text-xs">Action</Label>
              <Input
                id="f-action"
                value={draft.action ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, action: e.target.value || undefined })
                }
                placeholder="AI_QUERY"
                className="font-mono text-xs h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-outcome" className="text-xs">Outcome</Label>
              <Select
                value={draft.outcome ?? '__any__'}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    outcome: v === '__any__' ? undefined : (v as 'success' | 'failure'),
                  })
                }
              >
                <SelectTrigger id="f-outcome" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__" className="text-xs">Any</SelectItem>
                  <SelectItem value="success" className="text-xs">Success</SelectItem>
                  <SelectItem value="failure" className="text-xs">Failure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-category" className="text-xs">Category</Label>
              <Input
                id="f-category"
                value={draft.category ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, category: e.target.value || undefined })
                }
                placeholder="ai / tenant_lifecycle / …"
                className="font-mono text-xs h-8"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            {filtersDirty && (
              <Button variant="ghost" size="sm" onClick={() => setDraft(committed)}>
                Discard
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={clear}>
              <X className="h-3.5 w-3.5 mr-1" />
              Clear
            </Button>
            <Button size="sm" onClick={apply} disabled={!filtersDirty}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Body */}
      {isLoading && rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading audit events…
          </CardContent>
        </Card>
      ) : error ? (
        <PageError
          title="Failed to load audit stream"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Events</CardTitle>
            <CardDescription>
              Showing {rows.length} event{rows.length === 1 ? '' : 's'}.
              {data?.nextCursor ? ' More available.' : ' End of stream.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {rows.length === 0 ? (
              <EmptyState
                icon={Activity}
                title="No audit events"
                body="No platform events match the current filters."
                compact
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Outcome</TableHead>
                    {/* #608 — chain provenance column. Surfaces the
                        "in chain" vs "out of chain" distinction at row
                        level so legacy LOGIN_* rows don't get mistaken
                        for tamper-evident evidence. */}
                    <TableHead>Chain</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(r.createdAt).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium truncate max-w-[160px]" title={r.organizationName ?? undefined}>
                          {r.organizationName ?? '—'}
                        </div>
                        {r.organizationId && (
                          <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[160px]" title={r.organizationId}>
                            {r.organizationId.slice(0, 8)}…
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[160px]" title={r.userEmail ?? undefined}>
                        {r.userEmail ?? '—'}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{r.action}</code>
                        {r.category && (
                          <Badge variant="outline" className="ml-1 text-[10px]">{r.category}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <span className="font-mono">{r.resourceType}</span>
                        {r.resourceId && (
                          <div className="text-[10px] truncate max-w-[160px]" title={r.resourceId}>
                            {r.resourceId.slice(0, 8)}…
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <StatusPill
                          status={
                            r.outcome === 'success'
                              ? 'healthy'
                              : r.outcome === 'failure'
                                ? 'destructive'
                                : 'muted'
                          }
                          compact
                        >
                          {r.outcome ?? 'unknown'}
                        </StatusPill>
                      </TableCell>
                      {/* #608 — chain provenance badge. `inChain: true`
                          renders a small "in chain" badge that doubles
                          as a tooltip carrying the sequence number for
                          quick reference. `inChain: false` renders the
                          "out of chain" warning badge — legacy LOGIN_*
                          rows and pre-chain audit codepaths land here. */}
                      <TableCell className="text-xs">
                        {r.inChain ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-1 border-emerald-600/40 text-emerald-700 dark:text-emerald-400"
                            title={
                              r.sequenceNumber
                                ? `seq #${r.sequenceNumber}`
                                : 'hash-chained'
                            }
                          >
                            <ShieldCheck className="h-3 w-3" />
                            In chain
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-1 border-amber-600/40 text-amber-700 dark:text-amber-400"
                            title="This row was written directly to audit_logs without a recordHash / previousHash / sequenceNumber. Treat as legacy evidence — it is NOT tamper-evident chain proof. Common origin: auth LOGIN_* paths that bypass the canonical appendAdminAuditEvent helper."
                          >
                            <ShieldAlert className="h-3 w-3" />
                            Out of chain
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {data?.nextCursor && (
              <div className="flex justify-center py-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={isFetching}
                >
                  {isFetching ? 'Loading…' : 'Load 50 more'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
